/* ── Logo paths (served from /public) ── */
const logoLoopDimension  = '/logos/loopdimension.webp'
const logoHuawei         = '/logos/huawei.webp'
const logoCyberDefenders = '/logos/cyber_defenders.webp'
const logoHST            = '/logos/HST.webp'
const logoBanqueMisr     = '/logos/Banque_Misr.webp'
const logoACT            = '/logos/act.webp'
const logoZKTeco         = '/logos/Zkteco.webp'
const logoUdacity        = '/logos/udacity.webp'

export interface ExperienceEntry {
  id: number
  company: string
  role: string
  type: string          // Full-time / Part-time / Internship
  dateRange: string
  location: string
  locationType: string  // On-site / Remote / Hybrid
  achievements: string[]
  logo: string
}

const MONTHS: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
}

export function computeDuration(dateRange: string, referenceDate = new Date()): string {
  const [startStr, endStr] = dateRange.split('—').map(s => s.trim())
  const isActive = endStr === 'Present'
  const parse = (s: string): Date => {
    if (s === 'Present') return referenceDate
    const [mon, yr] = s.split(' ')
    return new Date(parseInt(yr), MONTHS[mon])
  }
  const start = parse(startStr)
  const end = parse(endStr)
  const totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth()) +
    (isActive ? 0 : 1)
  if (totalMonths < 1) return '< 1 mo'
  const years = Math.floor(totalMonths / 12)
  const mos = totalMonths % 12
  const parts: string[] = []
  if (years > 0) parts.push(`${years} yr${years > 1 ? 's' : ''}`)
  if (mos > 0) parts.push(`${mos} mo${mos > 1 ? 's' : ''}`)
  return parts.join(' ')
}

export const experiences: ExperienceEntry[] = [
  {
    id: 1,
    company: 'Loop Dimension',
    role: 'Software Engineer',
    type: 'Full-time',
    dateRange: 'Sep 2024 — Present',
    location: 'Seoul, South Korea',
    locationType: 'Remote',
    achievements: [
      'Building scalable software products for global users',
      'Full-stack + cloud engineering across Python services',
      'Shipped production features serving thousands of users',
    ],
    logo: logoLoopDimension,
  },
  {
    id: 2,
    company: 'Huawei ICT Academy',
    role: 'Ambassador & Instructor',
    type: 'Part-time',
    dateRange: 'Apr 2022 — Jun 2024',
    location: 'Giza, Egypt',
    locationType: 'Hybrid',
    achievements: [
      'Represented Egypt at MWC Barcelona 2023',
      'Organized ICT competitions & webinars across Egypt',
      'Taught AI fundamentals with the National Council for Women',
    ],
    logo: logoHuawei,
  },
  {
    id: 3,
    company: 'CyberDefenders',
    role: 'Associate Full Stack Engineer',
    type: 'Full-time',
    dateRange: 'Oct 2023 — Feb 2024',
    location: 'Delaware, United States',
    locationType: 'Remote',
    achievements: [
      'Built features for the blue-team training platform',
      'Full-stack development with Django + VueJS',
      'Optimized performance & squashed production bugs',
    ],
    logo: logoCyberDefenders,
  },
  {
    id: 4,
    company: 'HST',
    role: 'Software Engineer & AI Instructor',
    type: 'Full-time',
    dateRange: 'Nov 2022 — Nov 2023',
    location: 'New Cairo, Egypt',
    locationType: 'On-site',
    achievements: [
      'Engineered AI solutions — ML, NLP & computer vision',
      'Designed & taught the school’s AI curriculum for 1 year',
      'Presented AI research at conferences & journals',
    ],
    logo: logoHST,
  },
  {
    id: 5,
    company: 'Banque Misr',
    role: 'DevOps Intern',
    type: 'Internship',
    dateRange: 'Aug 2023 — Oct 2023',
    location: 'New Cairo, Egypt',
    locationType: 'Hybrid',
    achievements: [
      'Docker, Terraform & AWS infrastructure hands-on',
      'Built CI/CD pipelines in a banking environment',
    ],
    logo: logoBanqueMisr,
  },
  {
    id: 6,
    company: 'Advanced Computer Technology',
    role: 'POS Summer Intern',
    type: 'Internship',
    dateRange: 'Jul 2023 — Aug 2023',
    location: 'Cairo, Egypt',
    locationType: 'On-site',
    achievements: [
      'Built the full Krispy Kreme POS demo system',
      'Deployed EMC software for Micros Workstations + Oracle services',
    ],
    logo: logoACT,
  },
  {
    id: 7,
    company: 'ZKTeco North Africa',
    role: 'Software Engineer',
    type: 'Part-time',
    dateRange: 'Jun 2022 — Nov 2022',
    location: 'Giza, Egypt',
    locationType: 'On-site',
    achievements: [
      'Shipped 2 production systems — sales management & stock',
      'Mentored students in web development & Python',
    ],
    logo: logoZKTeco,
  },
  {
    id: 8,
    company: 'Udacity',
    role: 'Front-End Advocate',
    type: 'Full-time',
    dateRange: 'Mar 2020 — Jan 2022',
    location: 'Remote',
    locationType: 'Remote',
    achievements: [
      'Guided 400+ students to Front-End Nanodegree certification',
      'Instructed 6 cohorts across One Million Arab Coders (MENA)',
      'Ran weekly webinars tracking every student’s progress',
    ],
    logo: logoUdacity,
  },
]
